import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FolderIcon from "@mui/icons-material/Folder";
import BugReportIcon from "@mui/icons-material/BugReport";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EventNoteIcon from "@mui/icons-material/EventNote";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const features = [
  {
    icon: <FolderIcon sx={{ fontSize: 20 }} />,
    title: "Project Management",
    desc: "Create and track projects with team assignments and milestones",
    color: "#3B82F6",
  },
  {
    icon: <BugReportIcon sx={{ fontSize: 20 }} />,
    title: "Defect Tracking",
    desc: "Log, assign and resolve bugs with priority management",
    color: "#EF4444",
  },
  {
    icon: <AssessmentIcon sx={{ fontSize: 20 }} />,
    title: "Reports & Analytics",
    desc: "Generate Excel reports in Versatile Prime Infosoft format",
    color: "#10B981",
  },
  {
    icon: <EventNoteIcon sx={{ fontSize: 20 }} />,
    title: "Leave Management",
    desc: "Apply, approve and track team leaves with smart notifications",
    color: "#F59E0B",
  },
  {
    icon: <GroupIcon sx={{ fontSize: 20 }} />,
    title: "Team Management",
    desc: "Manage roles, birthdays, anniversaries and joining dates",
    color: "#8B5CF6",
  },
  {
    icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
    title: "Daily Status",
    desc: "Submit daily work updates and track team productivity",
    color: "#06B6D4",
  },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        background: "#060D1A",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background effects */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        {/* Top-left glow */}
        <Box
          sx={{
            position: "absolute",
            width: 700,
            height: 700,
            borderRadius: "50%",
            top: -300,
            left: -200,
            background:
              "radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 65%)",
          }}
        />
        {/* Bottom-right glow */}
        <Box
          sx={{
            position: "absolute",
            width: 600,
            height: 600,
            borderRadius: "50%",
            bottom: -200,
            right: -100,
            background:
              "radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 65%)",
          }}
        />
        {/* Center accent */}
        <Box
          sx={{
            position: "absolute",
            width: 400,
            height: 400,
            borderRadius: "50%",
            top: "30%",
            left: "35%",
            background:
              "radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 65%)",
          }}
        />
        {/* Grid */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)",
            backgroundSize: "50px 50px",
          }}
        />
        {/* Horizontal divider line */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "55%",
            width: 1,
            height: "100%",
            background:
              "linear-gradient(to bottom, transparent, rgba(255,255,255,0.06) 20%, rgba(255,255,255,0.06) 80%, transparent)",
          }}
        />
      </Box>

      {/* ── Left: Features ── */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", lg: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          px: { lg: 6, xl: 10 },
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 7 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #3B82F6 0%, #10B981 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 0 0 1px rgba(255,255,255,0.1), 0 8px 32px rgba(59,130,246,0.3)",
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 900,
                fontSize: "0.95rem",
                letterSpacing: "-0.5px",
              }}
            >
              PM
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.95rem",
                lineHeight: 1.1,
              }}
            >
              Versatile IT Solution
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.72rem" }}
            >
              Project Management Suite
            </Typography>
          </Box>
        </Box>

        {/* Headline */}
        <Box sx={{ mb: 6 }}>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "0.78rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.15em",
              mb: 1.5,
            }}
          >
            All-in-one platform
          </Typography>
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontSize: "2.6rem",
              lineHeight: 1.1,
              letterSpacing: "-1px",
              mb: 1.5,
            }}
          >
            Everything your
            <br />
            team needs,
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "2.6rem",
              lineHeight: 1.1,
              letterSpacing: "-1px",
              background: "linear-gradient(90deg, #3B82F6, #10B981)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            in one place.
          </Typography>
        </Box>

        {/* Feature grid */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
          {features.map((f, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 1.8,
                borderRadius: 2.5,
                bgcolor: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.06)",
                  borderColor: f.color + "40",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: f.color + "18",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: f.color,
                  flexShrink: 0,
                  border: `1px solid ${f.color}25`,
                }}
              >
                {f.icon}
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    lineHeight: 1.2,
                    mb: 0.3,
                  }}
                >
                  {f.title}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.35)",
                    fontSize: "0.72rem",
                    lineHeight: 1.4,
                  }}
                >
                  {f.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Right: Login Form ── */}
      <Box
        sx={{
          width: { xs: "100%", lg: "45%" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, sm: 6 },
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 420 }}>
          {/* Mobile logo */}
          <Box
            sx={{
              display: { xs: "flex", lg: "none" },
              alignItems: "center",
              gap: 1.5,
              mb: 5,
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #3B82F6, #10B981)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(59,130,246,0.3)",
              }}
            >
              <Typography
                sx={{ color: "#fff", fontWeight: 900, fontSize: "0.85rem" }}
              >
                PM
              </Typography>
            </Box>
            <Typography sx={{ color: "#fff", fontWeight: 700 }}>
              PM Tool
            </Typography>
          </Box>

          {/* Card */}
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.035)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 4,
              p: { xs: 3, sm: 4 },
              backdropFilter: "blur(24px)",
              boxShadow:
                "0 40px 100px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
            }}
          >
            {/* Top accent line */}
            <Box
              sx={{
                height: 2,
                background: "linear-gradient(90deg, #3B82F6, #10B981)",
                borderRadius: 1,
                mb: 3.5,
                mx: -4,
                mt: -4,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              }}
            />

            <Typography
              sx={{
                color: "#fff",
                fontWeight: 900,
                fontSize: "2rem",
                letterSpacing: "-0.5px",
                mb: 0.5,
              }}
            >
              Sign in
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.55)",
                fontSize: "0.88rem",
                mb: 3.5,
              }}
            >
              Enter your credentials to access the dashboard
            </Typography>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2.5,
                  bgcolor: "rgba(239,68,68,0.12)",
                  color: "#FCA5A5",
                  border: "1px solid rgba(239,68,68,0.25)",
                  borderRadius: 2,
                  "& .MuiAlert-icon": { color: "#FCA5A5" },
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              {/* Email */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    mb: 0.8,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Email address
                </Typography>
                <TextField
                  fullWidth
                  required
                  type="email"
                  placeholder="you@versatileit.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon
                          sx={{ color: "rgba(255,255,255,0.5)", fontSize: 17 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "rgba(255,255,255,0.1)",
                      borderRadius: 2,
                      color: "#fff",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.25)" },
                      "&:hover fieldset": {
                        borderColor: "rgba(59,130,246,0.7)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3B82F6",
                        borderWidth: 2,
                      },
                    },
                    "& input": {
                      py: 1.6,
                      fontSize: "0.95rem",
                      fontWeight: 500,
                    },
                    "& input::placeholder": {
                      color: "rgba(255,255,255,0.3)",
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              {/* Password */}
              <Box sx={{ mb: 3.5 }}>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    mb: 0.8,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Password
                </Typography>
                <TextField
                  fullWidth
                  required
                  type={showPw ? "text" : "password"}
                  placeholder="••••••••••"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon
                          sx={{ color: "rgba(255,255,255,0.5)", fontSize: 17 }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPw(!showPw)}
                          size="small"
                          sx={{
                            color: "rgba(255,255,255,0.5)",
                            "&:hover": { color: "#fff" },
                          }}
                        >
                          {showPw ? (
                            <VisibilityOffIcon
                              sx={{
                                fontSize: 17,
                                color: "rgba(255,255,255,0.5)",
                              }}
                            />
                          ) : (
                            <VisibilityIcon
                              sx={{
                                fontSize: 17,
                                color: "rgba(255,255,255,0.5)",
                              }}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "rgba(255,255,255,0.1)",
                      borderRadius: 2,
                      color: "#fff",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.25)" },
                      "&:hover fieldset": {
                        borderColor: "rgba(59,130,246,0.7)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3B82F6",
                        borderWidth: 2,
                      },
                    },
                    "& input": {
                      py: 1.6,
                      fontSize: "0.95rem",
                      fontWeight: 500,
                    },
                    "& input::placeholder": {
                      color: "rgba(255,255,255,0.3)",
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              {/* Button */}
              <Button
                fullWidth
                type="submit"
                disabled={loading}
                sx={{
                  py: 1.6,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  textTransform: "none",
                  background:
                    "linear-gradient(135deg, #3B82F6 0%, #10B981 100%)",
                  color: "#fff",
                  letterSpacing: "0",
                  boxShadow: "0 8px 32px rgba(59,130,246,0.3)",
                  "&:hover": {
                    boxShadow: "0 12px 40px rgba(59,130,246,0.45)",
                    transform: "translateY(-1px)",
                    background:
                      "linear-gradient(135deg, #2563EB 0%, #059669 100%)",
                  },
                  "&:active": { transform: "translateY(0)" },
                  transition: "all 0.2s",
                  "&.Mui-disabled": {
                    background: "rgba(255,255,255,0.06)",
                    color: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Sign In →"
                )}
              </Button>
            </Box>

            {/* Feature pills */}
            <Box
              sx={{
                mt: 3.5,
                pt: 3,
                borderTop: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.2)",
                  fontSize: "0.7rem",
                  textAlign: "center",
                  mb: 1.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                Includes
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.8,
                  justifyContent: "center",
                }}
              >
                {[
                  "Projects",
                  "Defects",
                  "Reports",
                  "Leaves",
                  "Daily Status",
                  "Team",
                ].map((f, i) => (
                  <Box
                    key={i}
                    sx={{
                      px: 1.2,
                      py: 0.4,
                      borderRadius: 5,
                      bgcolor: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.07)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.35)",
                        fontSize: "0.7rem",
                        fontWeight: 600,
                      }}
                    >
                      {f}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.15)",
              fontSize: "0.72rem",
              textAlign: "center",
              mt: 2.5,
            }}
          >
            Versatile Prime Infosoft Pvt. Ltd. © {new Date().getFullYear()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
